import { useTranslation } from 'react-i18next';

import * as St from './DesignOptionIn3DModal.styles';

import { UserDesignOperation } from '~/core/enums';
import { useCarousel } from '~/core/hooks/useCarousel';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import Icon3dViewNoCube from '~/icons/Icon3dViewNoCube.svg';
import DesignSectionModalArrow from '~molecules/DesignSectionModalArrow';

type DisplayData = {
  title: string;
  price: string;
  image: string;
};

type DesignOptionIn3DModalProps<T> = {
  options: { data: T; display: DisplayData }[];
  optionIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  selectOption: (option: T) => void;
  buttonText: string;
};

const DesignOptionIn3DModal = <T,>({
  options,
  optionIndex = 0,
  isOpen,
  onClose,
  selectOption,
  buttonText,
}: DesignOptionIn3DModalProps<T>) => {
  const { t } = useTranslation();
  const { registerUserAction } = useRegisterUserAction();

  const { currentIndex, prevIndex, nextIndex, goToNext, goToPrev } =
    useCarousel(
      options.map((option) => option.data),
      optionIndex
    );
  const { data: currentItem, display: currentElement } = options[currentIndex];
  const prevElement = options[prevIndex].display;
  const nextElement = options[nextIndex].display;

  const handleSelectOption = () => {
    selectOption(currentItem);
    onClose();
    registerUserAction(UserDesignOperation.MODIFY_EDGE);
  };

  return (
    <St.Container
      open={isOpen}
      onCancel={onClose}
      footer={
        <St.SelectButton
          key="submit"
          type="primary"
          onClick={handleSelectOption}
        >
          {buttonText}
        </St.SelectButton>
      }
    >
      <St.MainContent>
        <St.ModalTitle>{currentElement.title}</St.ModalTitle>
        <St.ModalText>{currentElement.price}</St.ModalText>
        <St.ModalText>{t('DESIGN_TOOLBAR.3D_MODAL_INFO')}</St.ModalText>
        <St.CarouselContainer>
          <DesignSectionModalArrow
            direction="left"
            imgUrl={prevElement.image}
            imgAlt={prevElement.title}
            description={prevElement.title}
            handleChange={goToPrev}
          />
          <St.ImgContainer
            src={currentElement.image}
            alt={currentElement.title}
          />
          <St.Icon3d component={() => <Icon3dViewNoCube />} />
          <DesignSectionModalArrow
            direction="right"
            imgUrl={nextElement.image}
            imgAlt={nextElement.title}
            description={nextElement.title}
            handleChange={goToNext}
          />
        </St.CarouselContainer>
      </St.MainContent>
    </St.Container>
  );
};

export default DesignOptionIn3DModal;
